"use client";

import React from 'react';
import { Search } from 'lucide-react';
import { flexRender, Table, SortingState } from '@tanstack/react-table';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ProgrammeResult } from "./ResultsTable";
import { ProgrammeSortControls } from "./ProgrammeSortControls";
import { UniversityFilterButtons } from "./UniversityFilterButtons";
import { ScoreQuartileFilterButtons, type ScoreQuartileRanges, type ScoreRange, type ScoreQuartileFilter } from "./ScoreQuartileFilterButtons";
import type { GatedFeature } from "./SignInRequired";

interface ResultsTableViewProps {
    isTableView: boolean;
    globalFilter: string;
    setGlobalFilter: (value: string) => void;
    universityFilter: string;
    setUniversityFilter: (value: string) => void;
    scoreQuartileRanges: ScoreQuartileRanges;
    availableQuartileRanges: Record<ScoreQuartileFilter, ScoreRange | null>;
    setScoreQuartileRanges: React.Dispatch<React.SetStateAction<ScoreQuartileRanges>>;
    isSignedIn: boolean;
    requireSignIn: (feature: GatedFeature, redirectTo?: string) => Promise<boolean>;
    sorting: SortingState;
    setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
    scrollContainerRef: React.RefObject<HTMLDivElement | null>;
    translate: (key: any) => string;
    table: Table<ProgrammeResult>;
    displayCount: number;
    hasMoreResultsToLoad: boolean;
    loadMoreResults: () => void;
    openProgrammeDialog: (result: ProgrammeResult) => void;
}

export const ResultsTableView: React.FC<ResultsTableViewProps> = ({
    isTableView,
    globalFilter,
    setGlobalFilter,
    universityFilter,
    setUniversityFilter,
    scoreQuartileRanges,
    availableQuartileRanges,
    setScoreQuartileRanges,
    isSignedIn,
    requireSignIn,
    sorting,
    setSorting,
    scrollContainerRef,
    translate,
    table,
    displayCount,
    hasMoreResultsToLoad,
    loadMoreResults,
    openProgrammeDialog,
}) => {
    return (
        <div className={`${isTableView ? 'flex flex-col flex-1 min-h-0' : 'hidden'}`}>
            {/* Search filter + sort buttons + institution filter */}
            <div className="mb-4 flex-shrink-0 flex flex-col gap-2">
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                    <div className="relative w-full min-w-0 sm:w-auto sm:flex-[1_1_20rem]">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-indigo-500" />
                        <input
                            type="text"
                            value={globalFilter ?? ''}
                            onChange={e => setGlobalFilter(e.target.value)}
                            placeholder={translate('searchProgrammes') || 'Search all columns...'}
                            className="w-full rounded-lg border-2 border-indigo-400 bg-indigo-50 py-2 pl-10 pr-4 text-indigo-900 placeholder-indigo-400 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                    <div className="flex min-w-0 flex-wrap items-center gap-2 sm:flex-[0_1_auto] sm:justify-end">
                        <ProgrammeSortControls sorting={sorting} setSorting={setSorting} translate={translate} />
                        <ScoreQuartileFilterButtons
                            ranges={scoreQuartileRanges}
                            availableRanges={availableQuartileRanges}
                            setRanges={setScoreQuartileRanges}
                            isSignedIn={isSignedIn}
                            requireSignIn={requireSignIn}
                            translate={translate}
                        />
                    </div>
                </div>
                <UniversityFilterButtons
                    universityFilter={universityFilter}
                    setUniversityFilter={setUniversityFilter}
                    translate={translate}
                />
            </div>
            <div
                ref={isTableView ? scrollContainerRef : undefined}
                id={isTableView ? "programme-results-scroll-container" : undefined}
                className="overflow-y-auto overflow-x-auto flex-1"
            >
                <InfiniteScroll
                    dataLength={Math.min(displayCount, table.getRowModel().rows.length)}
                    next={loadMoreResults}
                    hasMore={hasMoreResultsToLoad}
                    loader={null}
                    scrollThreshold="200px"
                    scrollableTarget="programme-results-scroll-container"
                >
                    <table id="tbl-prog-results" className="border-collapse text-sm w-full" style={{ tableLayout: 'auto' }}>
                        <thead>
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id} className="border-b bg-gray-50">
                                    {headerGroup.headers.map(header => (
                                        <th
                                            key={header.id}
                                            className={`p-2 font-semibold align-top`}
                                            style={header.column.id === 'programmeName' ? {} : {
                                                width: header.column.getSize(),
                                                minWidth: header.column.columnDef.minSize,
                                                maxWidth: header.column.columnDef.maxSize
                                            }}
                                        >
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.slice(0, displayCount).map((row, index) => {
                                const result = row.original;

                                return (
                                    <tr
                                        key={row.id}
                                        className={`border-b hover:bg-gray-100 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                        onClick={() => openProgrammeDialog(result)}
                                    >
                                        {row.getVisibleCells().map(cell => {
                                            const isCenteredColumn = [
                                                'favourite',
                                                'university',
                                                'jupas_id',
                                                'currentScore',
                                                'score2025Lower',
                                                'score2025Median',
                                                'score2025Upper',
                                                'recommendationRate'
                                            ].includes(cell.column.id);

                                            return (
                                                <td
                                                    key={cell.id}
                                                    className={`align-top p-2 ${isCenteredColumn ? 'text-center' : ''}`}
                                                    style={cell.column.id === 'programmeName' ? {
                                                        width: cell.column.getSize(),
                                                        minWidth: cell.column.columnDef.minSize,
                                                        maxWidth: cell.column.columnDef.maxSize
                                                    } : {
                                                        width: cell.column.getSize(),
                                                        minWidth: cell.column.columnDef.minSize,
                                                        maxWidth: cell.column.columnDef.maxSize
                                                    }}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </InfiniteScroll>

            </div>
        </div>
    );
};
